<template>
  <div class="legend">
    <span class="hint">{{ legendTitle }}：</span>
    <!-- 点分类本体 = 打开「修改分类」弹窗；点右侧 × = 删除（.stop 阻止冒泡到本体）。
         外观一律走共用的 CategoryChip —— 与「添加记录」弹窗里的分类是**同一个组件、同一份 CSS**，
         所以两处永远一模一样。 -->
    <CategoryChip
      v-for="cat in customCategories"
      :key="cat.key"
      class="legend-chip"
      :color="cat.color"
      :label="cat.label"
      editable
      has-action
      :title="'点击修改「' + parseName(cat.label).text + '」'"
      @click="startEdit(cat)"
    >
      <button class="delete-cat" title="删除该分类" @click.stop="confirmDelete(cat)">×</button>
    </CategoryChip>

    <span class="swatch-add" @click="startAdd">
      <span class="add-icon">+</span>
      自定义
    </span>

    <!-- 右侧「评论提醒 / 点赞提醒 / 通知 / 分享 / 下载 / 全屏」一栏只属于所有者自己的表格页，
         好友页复用本组件时用 show-actions=false 整块关掉。 -->
    <div v-if="showActions" class="legend-actions">
      <!-- 图标一律取自 Font Awesome 图标库（FaIcon），禁止手写 <svg> 画图标。 -->

      <!-- 刷新：重新拉取本周数据 + 分类 + 未读数（右上角第一个按钮，评论图标左边）。
           刷新期间图标旋转并禁用，避免连点重复请求。 -->
      <button
        class="icon-btn"
        type="button"
        :disabled="refreshing"
        :title="refreshing ? '刷新中…' : '刷新数据'"
        @click.stop="$emit('refresh')"
      >
        <FaIcon
          :icon="faArrowsRotate"
          :size="18"
          class="refresh-icon"
          :class="{ spinning: refreshing }"
        />
      </button>

      <!-- 评论提醒：朋友评论「我开放的时间表」后高亮，点击查看朋友评论了哪个时间块 -->
      <button class="icon-btn action-btn" title="评论提醒" @click.stop="$emit('open-notifications', 'COMMENT')">
        <FaIcon :icon="faCommentDots" :size="18" />
        <span v-if="commentUnread > 0" class="action-badge">{{ commentBadge }}</span>
      </button>

      <!-- 点赞提醒：朋友点赞「我开放的时间表」后高亮，点击查看朋友点赞了哪个时间块 -->
      <button class="icon-btn action-btn" title="点赞提醒" @click.stop="$emit('open-notifications', 'LIKE')">
        <FaIcon :icon="faThumbsUp" :size="18" />
        <span v-if="likeUnread > 0" class="action-badge">{{ likeBadge }}</span>
      </button>

      <!-- 通知：好友把时间表开放 / 调整权限 / 收回权限时给我的提醒 -->
      <button class="icon-btn action-btn" title="通知" @click.stop="$emit('open-notifications', 'SHARE')">
        <FaIcon :icon="faBell" :size="18" />
        <span v-if="shareUnread > 0" class="action-badge">{{ shareBadge }}</span>
      </button>

      <!-- 分享：弹窗选择对哪些好友开放（只读 / 编辑）当前时间表 -->
      <button class="icon-btn action-btn" title="分享" @click.stop="$emit('open-share')">
        <FaIcon :icon="faShareNodes" :size="18" />
      </button>

      <button class="icon-btn" title="下载" @click.stop="$emit('download')">
        <FaIcon :icon="faDownload" :size="18" />
      </button>
      <button class="icon-btn" title="全屏显示" @click.stop="$emit('fullscreen')">
        <FaIcon :icon="faExpand" :size="18" />
      </button>

    </div>

    <!-- 新增分类弹窗。
         ⚠️ 必须 append-to-body，否则「点开整页变黑、什么都点不了」。

         原因：el-dialog 的 .el-dialog__wrapper 是 position:fixed，但本组件被渲染在
         <div class="list-card"> 里面，而 **`.task-nav` 是 position:relative + z-index:100**
         （见 TaskNav.vue 的 .task-nav）—— 只要祖先链上出现 z-index + position 的元素，
         就创建了层叠上下文，弹窗的 wrapper 会被**关在** .task-nav 所在的层里（属于 .frame 层级），
         弹窗内部 z-index 再高也只是「局部老大」，压不过兄弟层。
         遮罩 .v-modal 直接挂在 body 上、z-index:2000，于是稳稳盖住整个 .frame 子树 ——
         包括那个被困在里面的弹窗。结果就是：一片黑 + 点不动。
         注：.task-nav 的 100 会胜出是因为它的祖先 <nav class="task-nav"> 与弹窗所在子树
         是同一个 .frame 下的**兄弟**层，同级就比 z-index。

         挂到 body 后弹窗脱离 .frame 的层叠上下文，与遮罩处在同一层，z-index 才真正生效
         （wrapper 2001 > .v-modal 2000，见 ElementUI popup/index.js:133 与 :154）。
         代价是**弹窗 DOM 不再挂在组件树里**：组件的 scoped 样式与 `>>>` 深度选择器都命中不了它，
         所以针对 .el-dialog/.el-input 等 ElementUI 内部节点的微调放在 App.vue 全局样式区
         （按 .cat-dialog 命名空间隔离），而不是写在下面的 <style scoped> 里。
         本组件内对 .cat-form / .cat-col / .color-grid 这些**自己模板里**的节点的 scoped 样式不受影响。

         布局：左右两列等宽（.cat-col 用 flex:1 1 0 严格均分）——
         左列放「分类颜色 + 分类图标」两块 .cat-pane（等高，保证大小一致），
         右列放「分类名称 + 分类预览 + 取消/确定」，按钮走 .cat-form-footer 而不是 el-dialog 的
         footer 插槽（插槽里的按钮会横跨整个弹窗，不在右列里）。 -->
    <el-dialog
      :title="isEditing ? '修改分类' : '添加分类'"
      :visible.sync="dialogVisible"
      width="720px"
      class="cat-dialog"
      append-to-body
      :close-on-click-modal="false"
    >
      <!-- 两列等宽：左列「分类颜色 + 分类图标」，右列「分类名称 + 分类预览 + 取消/确定」 -->
      <div class="cat-form">
        <!-- ===== 左列 ===== -->
        <div class="cat-col">
          <div class="form-block cat-pane">
            <div class="block-label">分类颜色</div>
            <div class="color-grid">
              <button
                v-for="c in tagColors"
                :key="c"
                type="button"
                class="color-swatch"
                :class="{ active: form.color === c }"
                :style="{ background: c }"
                :title="c"
                @click="form.color = c"
              >
                <i v-if="form.color === c" class="fas fa-check"></i>
              </button>
            </div>
            <div class="custom-color">
              <label class="custom-picker" :style="{ background: form.color }" title="选择自定义颜色">
                <input type="color" :value="form.color" @input="onPickCustomColor">
              </label>
              <el-input
                v-model="hexDraft"
                class="hex-input"
                size="mini"
                maxlength="7"
                placeholder="#a8e6cf"
                @change="onHexChange"
              />
            </div>
          </div>

          <div class="form-block cat-pane">
            <div class="block-label">
              分类图标
              <span class="block-note">（可选）</span>
              <button v-if="iconType" type="button" class="clear-icon-btn" @click="clearIcon">
                清除
              </button>
            </div>
            <!-- 图标库：分组平铺 + 滚动挑选（不提供搜索框，直接浏览更快） -->
            <div class="icon-scroll">
              <div v-for="g in iconGroups" :key="g.label" class="icon-group">
                <div class="group-label">{{ g.label }}</div>
                <div class="icon-grid">
                  <button
                    v-for="name in g.icons"
                    :key="name"
                    type="button"
                    class="icon-cell"
                    :class="{ active: iconType === 'fa' && iconValue === name }"
                    :title="name"
                    @click="pickIcon('fa', name)"
                  >
                    <i :class="'fas fa-' + name"></i>
                  </button>
                </div>
              </div>
            </div>
            <!-- 表情走手输：识别到表情就当成图标用 -->
            <el-input
              v-model="emojiDraft"
              size="mini"
              maxlength="8"
              placeholder="或输入表情，如 🚀"
              @input="onEmojiInput"
            />
          </div>
        </div>

        <!-- ===== 右列 ===== -->
        <div class="cat-col">
          <div class="form-block">
            <div class="block-label">分类名称</div>
            <el-input
              ref="nameInput"
              v-model="form.name"
              :maxlength="MAX_NAME_LEN"
              placeholder="例如：学习"
              @keyup.enter.native="submitForm"
            />
          </div>

          <div class="form-block cat-preview">
            <div class="block-label">分类预览</div>
            <div class="preview-box">
              <!-- 图例里的样子：色块/图标 + 名称。
                   样式直接复用 chipStyle（= 图例标签那一套），保证预览与真实图例一字不差；
                   图标 / 表情 / 色点三者的颜色统一取 `previewPalette.base`（分类原色），
                   与 CategoryChip 的 `iconColor` 同源。 -->
              <span
                class="pv-chip"
                :style="chipStyle(previewColor, previewName)"
              >
                <span
                  v-if="iconType === 'fa'"
                  class="pv-mark pv-icon"
                  :style="{ color: previewPalette.base }"
                ><i :class="'fas fa-' + iconValue"></i></span>
                <span
                  v-else-if="iconType === 'emoji'"
                  class="pv-mark pv-emoji"
                  :style="{ color: previewPalette.base }"
                >{{ iconValue }}</span>
                <span v-else class="pv-mark pv-dot" :style="{ background: previewPalette.base }"></span>
                <span class="pv-text">{{ previewText }}</span>
              </span>
              <!-- 表格色块里的样子：底色比色点浅一档、描边使用色点原色、文字与图例/时间块同色 -->
              <span
                class="pv-block"
                :style="{
                  background: previewPalette.blockBg,
                  borderColor: previewPalette.base,
                  color: blockTextColor
                }"
              >
                <i v-if="iconType === 'fa'" :class="'fas fa-' + iconValue"></i>
                <span v-else-if="iconType === 'emoji'">{{ iconValue }}</span>
                <span class="pv-text">{{ previewText }}</span>
              </span>
              <span class="pv-hint">上图为图例样式，下图为表格色块样式</span>
            </div>
          </div>

          <div class="cat-form-footer">
            <el-button size="small" @click="dialogVisible = false">取消</el-button>
            <el-button
              size="small"
              type="primary"
              :disabled="!form.name.trim()"
              @click="submitForm"
            >确定</el-button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  TAG_COLORS,
  DEFAULT_TAG_COLOR,
  TAG_ICON_GROUPS,
  MAX_TAG_NAME_LEN,
  buildTagName,
  parseTagName,
  leadingEmoji,
  normalizeHexColor,
  categoryPalette,
  BLOCK_TEXT_COLOR
} from './constants'
import CategoryChip from './CategoryChip.vue'
import FaIcon from '@/components/common/FaIcon.vue'
// 图标一律走 Font Awesome 图标库（子路径引入，只打包用到的几个），禁止手写 <svg> 画图标
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons/faArrowsRotate'
import { faCommentDots } from '@fortawesome/free-solid-svg-icons/faCommentDots'
import { faThumbsUp } from '@fortawesome/free-solid-svg-icons/faThumbsUp'
import { faBell } from '@fortawesome/free-solid-svg-icons/faBell'
import { faShareNodes } from '@fortawesome/free-solid-svg-icons/faShareNodes'
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload'
import { faExpand } from '@fortawesome/free-solid-svg-icons/faExpand'

export default {
  name: 'CategoryLegend',
  components: { CategoryChip, FaIcon },
  props: {
    customCategories: {
      type: Array,
      default: () => []
    },
    /** 评论类未读提醒数（来自后端互动通知）。>0 时「评论」按钮亮起红点。 */
    commentUnread: {
      type: Number,
      default: 0
    },
    /** 点赞类未读提醒数。>0 时「喜欢」按钮亮起红点。 */
    likeUnread: {
      type: Number,
      default: 0
    },
    /** 分享类未读提醒数（好友开放 / 调整 / 收回时间表权限）。>0 时「分享提醒」按钮亮起红点。 */
    shareUnread: {
      type: Number,
      default: 0
    },
    /**
     * 图例前缀文案。自己的表格页是「我的分类」，好友页复用时传「好友的分类」。
     */
    legendTitle: {
      type: String,
      default: '我的分类'
    },
    /**
     * 是否显示右侧动作区（评论提醒 / 点赞提醒 / 通知 / 分享 / 下载 / 全屏）。
     * 这些动作只对「所有者自己的表格页」有意义，好友页一律传 false。
     */
    showActions: {
      type: Boolean,
      default: true
    },
    /** 是否正在刷新：true 时刷新按钮禁用并让图标旋转 */
    refreshing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      // 正在编辑的分类 key（标签 ID）。空串 = 新增模式。
      editingKey: '',
      // 用户填的名称，**不含图标**。提交时才会把图标前缀拼进去。
      form: {
        name: '',
        color: DEFAULT_TAG_COLOR
      },
      MAX_NAME_LEN: MAX_TAG_NAME_LEN,
      // 选中的图标：type 为空串表示「不设图标」
      iconType: '',
      iconValue: '',
      // 图标库分组：搜索框已移除，直接平铺全部分组滚动挑选
      iconGroups: TAG_ICON_GROUPS,
      emojiDraft: '',
      // hex 输入框的草稿：直接用 v-model 绑 form.color 的话，
      // 输到一半的非法值（比如 "#a8e"）会立刻把颜色顶掉，所以先存草稿、失焦再校验
      hexDraft: DEFAULT_TAG_COLOR,
      tagColors: TAG_COLORS,
      // 图标定义挂到实例上，供模板 :icon 使用
      faArrowsRotate,
      faCommentDots,
      faThumbsUp,
      faBell,
      faShareNodes,
      faDownload,
      faExpand
    }
  },
  watch: {
    // 色板 / 取色器改了颜色时，把 hex 输入框同步过去
    'form.color'(v) {
      this.hexDraft = v
    }
  },
  computed: {
    /** 弹窗是「修改」还是「添加」—— 只影响标题与提交时 emit 的事件 */
    isEditing() {
      return !!this.editingKey
    },
    /** 评论未读角标：超过 99 显示 99+，避免数字把红点撑爆 */
    commentBadge() {
      return this.commentUnread > 99 ? '99+' : String(this.commentUnread)
    },
    /** 点赞未读角标 */
    likeBadge() {
      return this.likeUnread > 99 ? '99+' : String(this.likeUnread)
    },
    /** 分享未读角标 */
    shareBadge() {
      return this.shareUnread > 99 ? '99+' : String(this.shareUnread)
    },
    /** 预览用的颜色：手输 hex 非法时回退到默认色，避免预览区空白 */
    previewColor() {
      return normalizeHexColor(this.form.color) || DEFAULT_TAG_COLOR
    },
    /** 预览用的名称：没填时给个占位，让用户看清排版 */
    previewText() {
      return this.form.name.trim() || '分类名称'
    },
    /** 预览用的**整串名称**（含图标前缀）—— 传给 categoryPalette 才能命中默认分类的指定配色 */
    previewName() {
      return buildTagName(this.iconType, this.iconValue, this.previewText)
    },
    /** 预览的配色（图例胶囊 / 表格色块共用） */
    previewPalette() {
      return categoryPalette(this.previewColor, this.previewName)
    },
    /** 时间块 / 图例标签的统一文字色，暴露给模板（避免模板里再写一遍字面量） */
    blockTextColor() {
      return BLOCK_TEXT_COLOR
    }
  },
  methods: {
    /**
     * 分类 → 标签外观（底色 / 描边 / 文字色），走 constants 的 `categoryPalette`。
     * 只给这三个属性，描边的粗细与圆角由样式块管（图例标签在 `CategoryChip.vue`、
     * 弹窗预览在 `.pv-chip`），这样改描边样式不会失效。
     *
     * 现在**只剩弹窗预览 `.pv-chip` 在用**：图例上的分类胶囊已经换成共用组件
     * `CategoryChip.vue`（它内部自己调 categoryPalette），所以这个函数与
     * CategoryChip 的 chipStyle 必须保持同一套取值，否则「预览」和真实图例会对不上。
     *
     * ⚠️ 文字色**固定为 `BLOCK_TEXT_COLOR`（#2d3748，与时间块文字同色）**，
     *    不再取 `categoryPalette().text`（那档是「按分类色压暗到对比度达标」的派生色，
     *    每个分类深浅都不一样）。用户要求：不要再因为分类颜色而改变文字颜色。
     *    分类的辨识交给底色 `bg` / 描边 `border` / 图标，不靠文字色。
     */
    chipStyle(color, name) {
      const p = categoryPalette(color, name)
      return {
        background: p.bg,
        borderColor: p.border,
        color: BLOCK_TEXT_COLOR
      }
    },
    /**
     * 把后端存的 name 拆成 {icon, text}。
     * 图标库图标和表情都**存在分类名称字段里**，显示时拼成「颜色 + 图标 + 名称」。
     */
    parseName(name) {
      return parseTagName(name)
    },
    startAdd() {
      this.editingKey = ''
      this.form = { name: '', color: DEFAULT_TAG_COLOR }
      this.iconType = ''
      this.iconValue = ''
      this.emojiDraft = ''
      this.hexDraft = DEFAULT_TAG_COLOR
      this.openDialog()
    },
    /**
     * 打开「修改分类」弹窗：把后端存的整串 name 拆回「图标 + 纯名称」填进表单，
     * 颜色直接用该分类自己的颜色（不重置成默认色，否则一打开就"变色"很吓人）。
     */
    startEdit(cat) {
      const { iconType, iconValue, text } = parseTagName(cat.label)
      this.editingKey = cat.key
      this.form = {
        name: text,
        color: normalizeHexColor(cat.color) || DEFAULT_TAG_COLOR
      }
      this.iconType = iconType
      this.iconValue = iconValue
      // 表情图标要回显到表情输入框，否则用户看不到自己当初设的表情
      this.emojiDraft = iconType === 'emoji' ? iconValue : ''
      this.hexDraft = this.form.color
      this.openDialog()
    },
    openDialog() {
      this.dialogVisible = true
      this.$nextTick(() => {
        const input = this.$refs.nameInput
        if (input && input.focus) input.focus()
      })
    },
    pickIcon(type, value) {
      // 再点一次同一个 = 取消选择
      if (this.iconType === type && this.iconValue === value) {
        this.iconType = ''
        this.iconValue = ''
        return
      }
      this.iconType = type
      this.iconValue = value
      // 选了图标库图标就把表情输入框清掉，避免两个输入源各说各话
      this.emojiDraft = ''
    },
    clearIcon() {
      this.iconType = ''
      this.iconValue = ''
      this.emojiDraft = ''
    },
    onPickCustomColor(e) {
      this.form.color = e.target.value
    },
    /** 手输 hex：合法才写回颜色；非法就把输入框回滚到当前颜色，不留下误导性的残值 */
    onHexChange(v) {
      const hex = normalizeHexColor(v)
      if (hex) {
        this.form.color = hex
      } else {
        this.hexDraft = this.form.color
      }
    },
    /**
     * 表情输入框：**边输边判**（@input 而非 @change）。
     *
     * ⚠️ 判定必须走 constants 的 `leadingEmoji`（`\p{Extended_Pictographic}`），
     * 不能写成「码点 > 0x7F 就是表情」—— 那样中文/日文也会被当表情，
     * 与 `parseTagName` 的判定不一致，存进去再读出来就会错位
     * （曾导致「没选图标却把分类名第一个字显示成图标」）。
     *
     * 取**开头那一个完整 emoji**（而不是整串）当 iconValue：
     * 这样和 parseTagName 严格互逆，输入 '🚀🚀' 也不会把第二个 🚀 吃掉当名称。
     * 清空输入框 = 取消表情图标。
     */
    onEmojiInput(v) {
      const s = (v || '').trim()
      const emoji = leadingEmoji(s)
      if (!emoji) {
        if (this.iconType === 'emoji') {
          this.iconType = ''
          this.iconValue = ''
        }
        return
      }
      this.iconType = 'emoji'
      this.iconValue = emoji
    },
    /** 新增 / 修改共用一份表单，只在提交时按 isEditing 分岔 */
    submitForm() {
      const text = this.form.name.trim()
      if (!text) return
      const payload = {
        // ⚠️ 图标和名称一起塞进 name 字段（用户要求），颜色仍走 color 字段
        name: buildTagName(this.iconType, this.iconValue, text),
        color: normalizeHexColor(this.form.color) || DEFAULT_TAG_COLOR
      }
      if (this.isEditing) {
        this.$emit('update-category', { key: this.editingKey, ...payload })
      } else {
        this.$emit('add-category', payload)
      }
      this.dialogVisible = false
    },
    /**
     * 点 × 只负责「发起删除请求」，确认逻辑交给父组件：
     * - 分类没被时间块使用 → 父组件弹普通确认框；
     * - 分类已被时间块使用 → 父组件弹「删除时间块 / 转移到其他分类」选择框。
     * 两处逻辑不同，所以不能在这里写死一个 $confirm（否则会连着弹两个框）。
     */
    confirmDelete(cat) {
      this.$emit('delete-category', cat.key)
    }
  }
}
</script>

<style scoped>
.legend{
  display:flex;
  gap:0.75rem;
  flex-wrap:wrap;
  margin-bottom:0.875rem;
  font-size:0.813rem;
  align-items:center;
}
.legend .hint{
  color:var(--lp-text-2,#5f7268);
  font-size:0.75rem;
}
.legend-actions{
  display:flex;
  align-items:center;
  gap:0.5rem;
  margin-left:auto;
}
.icon-btn{
  width:32px;
  height:32px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#f5f1d5;
  border:none;
  border-radius:50%;
  padding:0;
  color:#35512d;
  cursor:pointer;
  box-shadow:none;
  transition:background 0.15s ease, transform 0.1s ease;
}
.icon-btn svg{
  width:18px;
  height:18px;
}
.icon-btn:hover{
  background:#e7f5c8;
}
.icon-btn:active{
  transform:translateY(2px);
}
/* 刷新图标：刷新中匀速转一圈，给「请求在跑」的即时反馈 */
.refresh-icon.spinning{
  animation:legend-refresh-spin 0.8s linear infinite;
}
@keyframes legend-refresh-spin{
  to{ transform:rotate(360deg); }
}
.icon-btn:disabled{
  cursor:default;
  opacity:0.7;
}
/* 新增的「评论 / 开放 / 喜欢」按钮与下载/全屏同款，但需要相对定位来挂角标 */
.action-btn{
  position:relative;
}
/* 未读红点角标：绝对定位在右上角，超过一位数自动放大 */
.action-badge{
  position:absolute;
  top:-4px;
  right:-4px;
  min-width:15px;
  height:15px;
  padding:0 4px;
  box-sizing:border-box;
  border-radius:8px;
  background:#e53e3e;
  color:#fff;
  font-size:10px;
  line-height:15px;
  text-align:center;
  font-family:inherit;
  box-shadow:0 0 0 2px var(--lp-surface,#fff);
  pointer-events:none;
}
/* 分类胶囊本体（.swatch / .dot / .dot-icon / .dot-emoji / .swatch-custom / .swatch-editable）
   已整体搬进共用组件 `CategoryChip.vue` —— 「我的分类」与「添加记录」弹窗共用同一份外观，
   这里只保留「自定义」按钮与删除按钮自己的样式。 */
.delete-cat{
  position:absolute;
  right:0.25rem;
  top:50%;
  transform:translateY(-50%);
  width:16px;
  height:16px;
  line-height:14px;
  border:none;
  background:transparent;
  color:#a0aec0;
  cursor:pointer;
  font-size:0.875rem;
  border-radius:50%;
  padding:0;
}
.delete-cat:hover{
  background:#fed7d7;
  color:#c53030;
}
/* 「自定义」按钮：与分类胶囊同尺寸同圆角，用虚线描边区分「新增」 */
.swatch-add{
  display:inline-flex;
  align-items:center;
  gap:0.375rem;
  padding:0.25rem 0.625rem;
  border:2px dashed var(--lp-border,#e3efe6);
  border-radius:0.5rem;
  background:var(--lp-surface,#fff);
  color:var(--lp-primary,#4aa46a);
  font-size:0.75rem;
  user-select:none;
  cursor:pointer;
  transition:.15s;
}
.swatch-add:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary,#4aa46a);
}
.add-icon{
  font-size:0.875rem;
  font-weight:600;
}

/* ===== 弹窗内容 ===== */
/* 两列等宽：flex:1 1 0 + width:0 让两列**严格均分**，不按内容多少分配宽度 */
.cat-form{
  display:flex;
  gap:1.25rem;
  align-items:stretch;
}
.cat-col{
  flex:1 1 0;
  width:0;
  min-width:0;
  display:flex;
  flex-direction:column;
  gap:0.875rem;
}
/* 分类颜色 / 分类图标 两块「大小一致」：固定同一高度，宽度随等宽列自然相同。
   两块内部都让主区域（色板 / 图标滚动区）flex 撑满，避免出现高度差。 */
.cat-pane{
  height:200px;
}
.form-block{
  display:flex;
  flex-direction:column;
  gap:0.5rem;
}
.block-label{
  display:flex;
  align-items:center;
  gap:0.25rem;
  font-size:0.813rem;
  font-weight:600;
  color:#26332b;
}
.block-note{
  font-weight:400;
  font-size:0.75rem;
  color:#94a59b;
}
.clear-icon-btn{
  margin-left:auto;
  border:none;
  background:transparent;
  padding:0;
  font-size:0.75rem;
  font-family:inherit;
  color:#94a59b;
  cursor:pointer;
}
.clear-icon-btn:hover{
  color:#d9534f;
}
/* 颜色候选：8 列自适应网格，24 色正好三行放完。
   色板 flex 撑满 .cat-pane 的剩余高度、行高用 1fr 均分 —— 列宽变宽后色块跟着变大，
   不再钉死 13px（那是两列窄布局下的旧值）。 */
.color-grid{
  flex:1 1 auto;
  min-height:0;
  display:grid;
  grid-template-columns:repeat(8,1fr);
  grid-auto-rows:1fr;
  gap:0.375rem;
}
.color-swatch{
  position:relative;
  width:100%;
  height:100%;
  min-width:0;
  border:1px solid rgba(0,0,0,.08);
  border-radius:0.1875rem;
  padding:0;
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:12px;
  line-height:1;
  color:rgba(0,0,0,.5);
  transition:transform .12s ease;
}
.color-swatch:hover{
  transform:scale(1.15);
}
/* 勾号收一点，别贴着色块边缘 */
.color-swatch i{
  transform:scale(.85);
}
.color-swatch.active{
  border-color:#2f7a4a;
  color:#1f5231;
  box-shadow:0 0 0 2px rgba(74,164,106,.35);
}
/* 自定义颜色行：取色器 + hex 输入框 */
.custom-color{
  display:flex;
  align-items:center;
  gap:0.375rem;
}
/* 原生取色器：把 input 铺满并透明化，外观完全由外层 label 的色块决定 */
.custom-picker{
  position:relative;
  width:26px;
  height:24px;
  flex:0 0 auto;
  border:2px solid rgba(0,0,0,.1);
  border-radius:0.375rem;
  overflow:hidden;
  cursor:pointer;
}
.custom-picker input{
  position:absolute;
  inset:-6px;
  width:200%;
  height:200%;
  border:none;
  padding:0;
  background:transparent;
  cursor:pointer;
  opacity:0;
}
/* hex 框吃掉自定义颜色行的剩余宽度 */
.hex-input{
  flex:1 1 auto;
  min-width:0;
}
/* 图标选择：滚动区 flex 撑满 .cat-pane 的剩余高度（不再用 max-height，
   否则两块面板的高度会随内容浮动，做不到「大小一致」） */
.icon-scroll{
  flex:1 1 auto;
  min-height:0;
  overflow-y:auto;
  border:2px solid #e3efe6;
  border-radius:0.625rem;
  padding:0.5rem;
}
.icon-group + .icon-group{
  margin-top:0.5rem;
}
.group-label{
  font-size:0.688rem;
  color:#94a59b;
  margin-bottom:0.25rem;
}
.icon-grid{
  display:grid;
  grid-template-columns:repeat(8,1fr);
  gap:0.25rem;
}
.icon-cell{
  width:100%;
  aspect-ratio:1/1;
  min-width:0;
  display:flex;
  align-items:center;
  justify-content:center;
  border:2px solid transparent;
  border-radius:0.375rem;
  background:transparent;
  color:#5f7268;
  font-size:15px;
  cursor:pointer;
  padding:0;
  transition:background .12s ease;
}
.icon-cell:hover{
  background:#f1f5f2;
}
.icon-cell.active{
  background:#e9f5ec;
  border-color:#4aa46a;
  color:#2f7a4a;
}
/* ===== 分类预览（右列） ===== */
/* 预览块吃掉右列剩余高度，顶到与左列底边齐平 */
.cat-preview{
  flex:1 1 auto;
  min-height:0;
}
.preview-box{
  flex:1 1 auto;
  min-height:0;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap:0.75rem;
  padding:0.75rem;
  border:2px dashed #e3efe6;
  border-radius:0.75rem;
  background:#f7fbf8;
}
/* 图例样式：白底小胶囊 + 色块/图标 + 名称 */
.pv-chip{
  display:inline-flex;
  align-items:center;
  gap:0.5rem;
  max-width:100%;
  padding:0.5rem 0.875rem;
  border:2px solid #e3efe6;
  border-radius:0.625rem;
  background:#fff;
  color:#26332b;
  font-size:1rem;
}
.pv-mark{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  flex:0 0 auto;
  line-height:1;
}
.pv-dot{
  width:18px;
  height:18px;
  border-radius:0.25rem;
  border:1px solid rgba(0,0,0,.15);
}
.pv-icon{
  font-size:22px;
}
.pv-emoji{
  font-size:20px;
}
.pv-text{
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}
/* 表格色块样式：整块铺所选颜色，文字按同色压暗保证可读 */
.pv-block{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:0.375rem;
  max-width:100%;
  padding:0.625rem 1rem;
  border:1px solid rgba(0,0,0,.08);
  border-radius:0.625rem;
  font-size:0.9375rem;
}
.pv-hint{
  font-size:0.688rem;
  color:#94a59b;
  text-align:center;
}
/* 取消 / 确定：放进右列底部（不再走 el-dialog 的 footer 插槽） */
.cat-form-footer{
  display:flex;
  justify-content:flex-end;
  gap:0.5rem;
  margin-top:auto;
}
/* 窄屏放不下两列就纵向堆叠；注意此时 flex-basis 作用在高度上，
   必须把 .cat-col 的 flex:1 1 0 / width:0 撤掉，否则列会被压成 0 高。 */
@media (max-width:640px){
  .cat-form{
    flex-direction:column;
  }
  .cat-col{
    flex:0 0 auto;
    width:auto;
  }
  .cat-pane{
    height:190px;
  }
  .cat-preview{
    min-height:150px;
  }
}

</style>

<style scoped>
@media (max-width:700px){
  .legend{gap:0.625rem;}
  .hint{width:100%;}
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边，尺寸与间距全部沿用上面的原值。 */
.icon-btn{
  background:transparent;
  border-radius:0.625rem;
  color:var(--lp-text-3,#94a59b);
  transition:background 0.15s ease, color 0.15s ease, transform 0.1s ease;
}
.icon-btn:hover{
  background:var(--lp-primary-soft,#e9f5ec);
  color:var(--lp-primary-dark,#2f7a4a);
}
.icon-btn:active{
  transform:translateY(1px);
}
/* 分类胶囊的描边/圆角/hover 已随样式搬进 CategoryChip.vue，这里不再重复覆盖 */
.delete-cat{
  color:var(--lp-text-3,#94a59b);
}
.delete-cat:hover{
  background:#fdecec;
  color:#d9534f;
}
.block-label{
  color:var(--lp-text,#26332b);
}
.block-note,
.group-label{
  color:var(--lp-text-3,#94a59b);
}
.clear-icon-btn{
  color:var(--lp-text-3,#94a59b);
}
.icon-scroll{
  border-color:var(--lp-border,#e3efe6);
}
.icon-cell{
  color:var(--lp-text-2,#5f7268);
}
.icon-cell:hover{
  background:var(--lp-surface-2,#f7fbf8);
}
.icon-cell.active{
  background:var(--lp-primary-soft,#e9f5ec);
  border-color:var(--lp-primary,#4aa46a);
  color:var(--lp-primary-dark,#2f7a4a);
}
.color-swatch.active{
  border-color:var(--lp-primary-dark,#2f7a4a);
  color:var(--lp-primary-dark,#2f7a4a);
}
.preview-box{
  border-color:var(--lp-border,#e3efe6);
  background:var(--lp-surface-2,#f7fbf8);
}
.pv-chip{
  border-color:var(--lp-border,#e3efe6);
  background:var(--lp-surface,#fff);
  color:var(--lp-text,#26332b);
}
.pv-hint{
  color:var(--lp-text-3,#94a59b);
}
</style>
